'use client';

import { useState } from 'react';
import { patchMe, type DeadlineStance, type ProfileApiError as ProfileApiErrorType } from '@/lib/profile/api';
import type { DashboardResponse } from '@/lib/dashboard/api';
import { track } from '@/lib/analytics/events';

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Post-onboarding goal editor (issue #297): a small inline form, collapsed
 * by default, letting the learner change mode later — including clearing a
 * date — without touching XP/streak/mastery state (this only ever PATCHes
 * `/v1/me` profile fields). `new_atoms_per_day` is only sent when the
 * learner actually edits it in this session: the dashboard has no read path
 * for the stored value, so sending an untouched default would silently
 * overwrite whatever the learner set earlier.
 */
export default function GoalEditor({
  dashboard,
  sessionAuth,
  onSaved,
}: {
  dashboard: DashboardResponse;
  sessionAuth: { idToken: string; identityId: string };
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<DeadlineStance>(
    dashboard.exam.exam_date === null ? 'no_date' : 'has_date',
  );
  const [date, setDate] = useState(dashboard.exam.exam_date ?? todayIsoDate());
  const [newAtomsPerDay, setNewAtomsPerDay] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        className="home-goal-btn"
        data-testid="goal-editor-open"
        onClick={() => setOpen(true)}
      >
        Change goal
      </button>
    );
  }

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await patchMe(
        sessionAuth.idToken,
        sessionAuth.identityId,
        mode === 'no_date'
          ? { deadline_stance: 'no_date', ...(newAtomsPerDay !== null ? { new_atoms_per_day: newAtomsPerDay } : {}) }
          : { exam_date: date, ...(newAtomsPerDay !== null ? { new_atoms_per_day: newAtomsPerDay } : {}) },
      );
      track('goal_updated', { deadline_stance: mode });
      setOpen(false);
      onSaved();
    } catch (err) {
      const apiError = err as ProfileApiErrorType;
      setError(apiError.message ?? 'Could not save your goal.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="home-card" data-testid="goal-editor">
      <h2 className="home-card-title">Change your goal</h2>
      <div className="home-goal-mode-choice">
        <button
          type="button"
          className="home-goal-mode-btn"
          aria-pressed={mode === 'has_date'}
          disabled={submitting}
          onClick={() => setMode('has_date')}
        >
          Exam date
        </button>
        <button
          type="button"
          className="home-goal-mode-btn"
          aria-pressed={mode === 'no_date'}
          disabled={submitting}
          onClick={() => setMode('no_date')}
        >
          No date
        </button>
      </div>
      {mode === 'has_date' && (
        <label className="home-goal-field" htmlFor="goal-editor-exam-date">
          Exam date
          <input
            id="goal-editor-exam-date"
            type="date"
            min={todayIsoDate()}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
      )}
      <label className="home-goal-field" htmlFor="goal-editor-new-atoms-per-day">
        New things per day
        <input
          id="goal-editor-new-atoms-per-day"
          type="number"
          min={1}
          step={1}
          placeholder="Leave blank to keep your current pace"
          value={newAtomsPerDay ?? ''}
          onChange={(event) => {
            const parsed = Number.parseInt(event.target.value, 10);
            setNewAtomsPerDay(Number.isNaN(parsed) ? null : Math.max(1, parsed));
          }}
        />
      </label>
      {error && (
        <p role="alert" className="home-goal-error">
          {error}
        </p>
      )}
      <div className="home-goal-btn-row">
        <button
          type="button"
          className="home-goal-btn home-goal-btn-primary"
          disabled={submitting}
          onClick={() => void handleSave()}
        >
          Save
        </button>
        <button
          type="button"
          className="home-goal-btn"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </section>
  );
}
